import { Box, Skeleton, Typography, useTheme, alpha } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import React, { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, Sector } from 'recharts';
import { useFormatter } from '../../context/FormatterContext';

// Color palettes for charts
const assetAllocationColors = [
    '#FF4900', '#545454', '#8fd351', '#256561', '#FFCA27'
];

interface AssetAllocationProps {
    data: Array<{
        symbol: string;
        percentage: number;
        color: string | undefined;
    }>;
    isLoading?: boolean;
}

const AssetAllocation: React.FC<AssetAllocationProps> = ({ data, isLoading = false }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { formatPercentage } = useFormatter();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // Sort data by percentage in descending order
    const sortedData = [...data]
        .filter(item => item.percentage > 0) // Filter out exactly 0% allocations
        .sort((a, b) => b.percentage - a.percentage);

    // Define threshold for small assets
    const SMALL_ASSETS_THRESHOLD = 0.5; // Assets below 0.5% will be grouped
    
    // Separate small assets from main assets
    const mainAssets = sortedData.filter(item => item.percentage >= SMALL_ASSETS_THRESHOLD);
    const smallAssets = sortedData.filter(item => item.percentage < SMALL_ASSETS_THRESHOLD);
    
    // Take top 5 from main assets (excluding small assets)
    const processedData = mainAssets.slice(0, 5);
    
    // Add "Others" category for main assets beyond top 5
    if (mainAssets.length > 5) {
        const othersPercentage = mainAssets.slice(5).reduce((sum, item) => sum + item.percentage, 0);
        if (othersPercentage > 0) {
            processedData.push({
                symbol: t('dashboard.assetAllocation.others'),
                percentage: othersPercentage,
                color: '#4D4D4D', // Dark gray for Others
            });
        }
    }
    
    // Add "Small Assets" category if there are any small assets
    if (smallAssets.length > 0) {
        const smallAssetsPercentage = smallAssets.reduce((sum, item) => sum + item.percentage, 0);
        processedData.push({
            symbol: t('dashboard.assetAllocation.smallAssets'),
            percentage: smallAssetsPercentage,
            color: '#6E6E6E', // Medium gray for Small Assets
        });
    }

    // Format data for the pie chart
    const chartData = processedData.map((item, index) => {
        // Handle special cases for currency symbols
        let displayName = item.symbol;
        
        // Specifically handle the case where we have a currency symbol
        if (displayName === '&euro;') {
            displayName = 'EUR';
        }
        
        return {
            name: displayName,
            value: item.percentage,
            color: item.color || assetAllocationColors[index],
            originalSymbol: item.symbol // Keep the original for debugging
        };
    });

    // Enhanced tooltip with more information
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            const isSmallAssets = data.name === 'Small Assets';
            
            return (
                <Box sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    padding: 1.5,
                    boxShadow: theme.shadows[2],
                    minWidth: '150px',
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: data.color }}>
                        {data.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t('dashboard.assetAllocation.allocation', { percentage: formatPercentage(data.value) })}
                    </Typography>
                    {isSmallAssets && smallAssets.length > 0 && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                {t('dashboard.assetAllocation.includes')}
                            </Typography>
                            <Box sx={{ mt: 0.5, maxHeight: '80px', overflowY: 'auto' }}>
                                {smallAssets.map((asset, i) => (
                                    <Typography key={i} variant="caption" display="block">
                                        {t('dashboard.assetAllocation.tooltipFormat', {
                                            symbol: asset.symbol,
                                            percentage: formatPercentage(asset.percentage)
                                        })}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        }
        return null;
    };

    // Active shape component for hover effect
    const renderActiveShape = (props: any) => {
        const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
        
        return (
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 10}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <Sector
                    cx={cx}
                    cy={cy}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    innerRadius={innerRadius - 5}
                    outerRadius={innerRadius - 2}
                    fill={fill}
                />
            </g>
        );
    };

    // Handle pie sector hover
    const onPieEnter = (_: any, index: number) => {
        setActiveIndex(index);
    };

    // Handle pie sector leave
    const onPieLeave = () => {
        setActiveIndex(null);
    };

    // Handle legend item click
    const handleLegendClick = (index: number) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    // Skeleton for the asset allocation chart
    const AllocationSkeleton = () => (
        <>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="calc(100% - 40px)"
                minHeight="260px"
            >
                <Skeleton variant="circular" width={200} height={200} sx={{ mb: 2 }} />
                <Box width="100%" display="flex" justifyContent="space-around" mt={2}>
                    {[...Array(5)].map((_, i) => (
                        <Box key={i} display="flex" flexDirection="column" alignItems="center">
                            <Skeleton variant="text" width={40} height={20} />
                            <Skeleton variant="text" width={30} height={16} />
                        </Box>
                    ))}
                </Box>
            </Box>
        </>
    );

    // Pie chart configuration
    const pieChartDefaults = {
        innerRadius: 60,
        outerRadius: 80,
        paddingAngle: 2,
        animationDuration: 800
    };

    // Chart container margin
    const containerMargin = { top: 0, right: 0, bottom: 0, left: 0 };

    return (
        <Box sx={{
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: theme.palette.divider,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
        }}>
            {isLoading ? (
                <AllocationSkeleton />
            ) : chartData.length === 0 ? (
                // Show this message when there are no assets with positive allocation
                <>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        minHeight: '200px',
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            No assets with positive allocation found.
                        </Typography>
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flex: 1,
                        position: 'relative',
                    }}>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart margin={containerMargin}>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={pieChartDefaults.innerRadius}
                                    outerRadius={pieChartDefaults.outerRadius}
                                    paddingAngle={pieChartDefaults.paddingAngle}
                                    animationDuration={pieChartDefaults.animationDuration}
                                    stroke="none"
                                    activeIndex={activeIndex !== null ? activeIndex : undefined}
                                    activeShape={renderActiveShape}
                                    onMouseEnter={onPieEnter}
                                    onMouseLeave={onPieLeave}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            justifyContent: 'center', 
                            mt: 2,
                            gap: 1.5,
                            width: '100%',
                        }}>
                            {chartData.map((item, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        mr: 2,
                                        cursor: 'pointer',
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 1,
                                        backgroundColor: activeIndex === index ? alpha(item.color, 0.1) : 'transparent',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: alpha(item.color, 0.1),
                                        }
                                    }}
                                    onClick={() => handleLegendClick(index)}
                                >
                                    <Box 
                                        sx={{ 
                                            width: 12, 
                                            height: 12, 
                                            bgcolor: item.color,
                                            mr: 1,
                                            borderRadius: 0.5
                                        }} 
                                    />
                                    <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{ 
                                            fontWeight: activeIndex === index ? 'bold' : 'normal',
                                        }}
                                    >
                                        {item.name} ({formatPercentage(item.value)})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AssetAllocation;
